import React from 'react';
import { useUser } from '../context/UserContext';
import { Award, Zap, Star } from 'react-feather';

const FeatureGuard = ({
  feature,
  children,
  fallback = null,
  showUpgradePrompt = true,
  requiredPlan = null,
  className = ""
}) => {
  const { hasFeatureAccess, getPlanDisplayName } = useUser();

  // If user has access, render children
  if (hasFeatureAccess(feature)) {
    return <div className={className} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>{children}</div>;
  }

  // If no upgrade prompt should be shown, render fallback
  if (!showUpgradePrompt) {
    return fallback;
  }

  // Determine which plan is required
  const getRequiredPlanInfo = () => {
    if (requiredPlan) {
      switch (requiredPlan) {
        case 'starter':
          return {
            name: 'Starter',
            price: '$24/month',
            icon: <Zap className="h-5 w-5" />,
            color: 'text-blue-400',
            bgColor: 'bg-blue-500/10',
            borderColor: 'border-blue-500/20'
          };
        case 'pro':
          return {
            name: 'Pro',
            price: '$38/month',
            icon: <Star className="h-5 w-5" />,
            color: 'text-purple-400',
            bgColor: 'bg-purple-500/10',
            borderColor: 'border-purple-500/20'
          };
        case 'unlimited':
          return {
            name: 'Unlimited',
            price: '$125/month',
            icon: <Zap className="h-5 w-5" />,
            color: 'text-orange-400',
            bgColor: 'bg-orange-500/10',
            borderColor: 'border-orange-500/20'
          };
        case 'lifetime':
          return {
            name: 'Lifetime',
            price: '$947 one-time',
            icon: <Award className="h-5 w-5" />,
            color: 'text-emerald-400',
            bgColor: 'bg-emerald-500/10',
            borderColor: 'border-emerald-500/20'
          };
        default:
          return {
            name: 'Pro',
            price: '$38/month',
            icon: <Star className="h-5 w-5" />,
            color: 'text-purple-400',
            bgColor: 'bg-purple-500/10',
            borderColor: 'border-purple-500/20'
          };
      }
    }

    // Auto-determine based on feature
    switch (feature) {
      case 'voicebot':
      case 'pro_settings':
      case 'advanced_customization':
      case 'white_labeling':
      case 'external_integrations':
        return {
          name: 'Pro',
          price: '$38/month',
          icon: <Star className="h-5 w-5" />,
          color: 'text-purple-400',
          bgColor: 'bg-purple-500/10',
          borderColor: 'border-purple-500/20'
        };
      case 'analytics':
      case 'url_scraping':
      case 'api_access':
        return {
          name: 'Starter', // Available in Starter too (basic analytics)
          price: '$24/month',
          icon: <Zap className="h-5 w-5" />,
          color: 'text-blue-400',
          bgColor: 'bg-blue-500/10',
          borderColor: 'border-blue-500/20'
        };
      case 'priority_support':
      case 'custom_training':
        return {
          name: 'Lifetime',
          price: '$947 one-time',
          icon: <Award className="h-5 w-5" />,
          color: 'text-emerald-400',
          bgColor: 'bg-emerald-500/10',
          borderColor: 'border-emerald-500/20'
        };
      default:
        return {
          name: 'Pro',
          price: '$38/month',
          icon: <Star className="h-5 w-5" />,
          color: 'text-purple-400',
          bgColor: 'bg-purple-500/10',
          borderColor: 'border-purple-500/20'
        };
    }
  };

  const planInfo = getRequiredPlanInfo();

  const getFeatureDisplayName = () => {
    const featureNames = {
      'chatbot_builder': 'Chatbot Builder',
      'dashboard': 'Dashboard',
      'transcripts': 'Transcripts',
      'file_upload': 'File Upload',
      'url_scraping': 'URL Scraping',
      'voicebot': 'AI Voicebot',
      'pro_settings': 'Pro+ Settings',
      'analytics': 'Analytics Dashboard',
      'basic_customization': 'Basic Customization',
      'advanced_customization': 'Advanced Customization',
      'white_labeling': 'White Labeling',
      'external_integrations': 'External Integrations',
      'api_access': 'API Access',
      'priority_support': 'Priority Support',
      'custom_training': 'Custom Training'
    };
    return featureNames[feature] || 'Premium Feature';
  };

  return (
    <div className={`w-full h-full flex items-center justify-center p-6 ${className}`}>
      <div 
        className="relative overflow-hidden w-full max-w-2xl bg-white rounded-3xl"
        style={{
          boxShadow: '0 20px 40px -15px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.05)'
        }}
      >
        {/* Top Gradient Banner */}
        <div className={`h-32 w-full ${planInfo.bgColor} relative overflow-hidden flex items-center justify-center`}>
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at center, rgba(255,255,255,0.8) 0%, transparent 70%)' }}></div>
          <div className={`w-16 h-16 rounded-2xl bg-white shadow-xl flex items-center justify-center transform -rotate-6 transition-transform hover:rotate-0 duration-300 z-10`}>
            {React.cloneElement(planInfo.icon, { className: `w-8 h-8 ${planInfo.color}` })}
          </div>
        </div>

        <div className="px-8 pt-8 pb-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">
              Unlock {getFeatureDisplayName()}
            </h2>
            <p className="text-slate-500 text-base max-w-md mx-auto leading-relaxed">
              Enhance your workflow with powerful tools. This feature is exclusively available in the <span className={`font-semibold ${planInfo.color}`}>{planInfo.name}</span> plan.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between bg-slate-50 rounded-2xl p-5 mb-8 border border-slate-100">
            <div className="flex items-center space-x-4 mb-4 sm:mb-0">
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Your Current Plan</span>
                <div className="flex items-center">
                  <span className="px-3 py-1 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 shadow-sm">
                    {getPlanDisplayName()}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="hidden sm:flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-sm border border-slate-100 mx-4">
              <svg className="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex flex-col text-right sm:text-left">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Required Plan</span>
                <div className="flex items-center justify-end sm:justify-start">
                  <span className={`px-3 py-1 ${planInfo.bgColor} ${planInfo.color} border ${planInfo.borderColor} rounded-full text-sm font-medium shadow-sm`}>
                    {planInfo.name}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center">
            <button
              onClick={() => { window.location.href = '/#pricing'; }}
              className="group relative inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold text-white transition-all duration-200 bg-slate-900 border border-transparent rounded-full hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 shadow-lg hover:shadow-xl w-full sm:w-auto"
            >
              <span>Upgrade to {planInfo.name}</span>
              <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path></svg>
            </button>
            <p className="mt-4 text-xs text-slate-400 font-medium">
              Starting at {planInfo.price}. Cancel anytime.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureGuard; 